<script setup lang="ts">
import { ref, computed } from 'vue';
import { XMarkIcon, IdentificationIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';

interface Props {
  isOpen: boolean;
  previousGrade?: string;
  currentGrade?: string;
  changeType?: 'grade' | 'school';
}

interface Emits {
  (e: 'close'): void;
  (e: 'upload'): void;
  (e: 'dismiss'): void;
}

const props = withDefaults(defineProps<Props>(), {
  changeType: 'grade'
});

const emit = defineEmits<Emits>();

const changeTitle = computed(() => {
  return props.changeType === 'school' ? 'Your school has been updated!' : 'Your grade level has been updated!';
});

const changeDescription = computed(() => {
  if (props.previousGrade && props.currentGrade) {
    if (props.changeType === 'school') {
      return `You've changed from <strong>${props.previousGrade}</strong> to <strong>${props.currentGrade}</strong>.`;
    } else {
      return `You've changed from <strong>${props.previousGrade}</strong> to <strong>${props.currentGrade}</strong>.`;
    }
  }
  return props.changeType === 'school' ? "You've updated your school." : "You've updated your grade level.";
});

const idRequirement = computed(() => {
  if (props.changeType === 'school') {
    return `showing your school name (${props.currentGrade || 'updated school'})`;
  }
  return `showing your current grade level (${props.currentGrade || 'updated grade'})`;
});

const handleUpload = () => {
  emit('upload');
};

const handleDismiss = () => {
  emit('dismiss');
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-2xl shadow-[8px_8px_0_0_rgba(0,0,0,1)] border-2 border-black max-w-md w-full transform transition-all"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 pt-6">
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon class="w-7 h-7 text-orange-600" />
            </div>
            <h2 class="text-2xl font-bold text-gray-900">Update Your ID</h2>
          </div>
          <button
            @click="handleClose"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- Content -->
        <div class="px-6 py-6 space-y-4">
          <!-- Alert Box -->
          <div class="p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
            <div class="flex gap-3">
              <IdentificationIcon class="w-6 h-6 text-orange-700 flex-shrink-0 mt-0.5" />
              <div>
                <p class="text-sm font-semibold text-orange-900">
                  {{ changeTitle }}
                </p>
                <p class="text-sm text-orange-800" v-html="changeDescription"></p>
              </div>
            </div>
          </div>

          <!-- Instructions -->
          <div class="space-y-3">
            <p class="text-gray-700 leading-relaxed">
              To ensure your profile is up-to-date and verify your identity, please upload <strong>both</strong> of the following:
            </p>
            
            <div class="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <p class="text-sm text-blue-900 font-medium mb-2">What to Upload:</p>
              <ul class="text-sm text-blue-800 space-y-2 ml-4 list-decimal">
                <li><strong>Selfie with School ID:</strong> A clear photo of yourself holding your school ID next to your face</li>
                <li><strong>School ID Only:</strong> A clear photo or scan of your school ID {{ idRequirement }}</li>
              </ul>
            </div>

        

        
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 px-6 pb-6">
          <button
            @click="handleDismiss"
            class="px-5 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Maybe Some Other Time
          </button>
          <button
            @click="handleUpload"
            class="px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-md flex items-center gap-2"
          >
            <IdentificationIcon class="w-5 h-5" />
            Upload ID
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.9) translateY(-20px);
}
</style>

